import React, { useEffect, useState } from "react";
import useSWR from "swr";
import axios from "axios";
import { Form, FormInstance, message, UploadProps, notification, UploadFile, Upload, Modal } from "antd";
import { useAuth } from "../context/AuthContext";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";

export interface WithServiceItemMasterLogicProps {
    form: FormInstance<any>;
    brands: { label: string; value: string }[];
    models: { label: string; value: string }[];
    warehouseName: string;
    handleSubmit: () => void;
    handleExportTemplate: () => void;
    isImportModalOpen: boolean;
    handleOpenImportModal: () => void;
    handleCloseImportModal: () => void;
    uploadProps: UploadProps;
    handleImportSubmit: () => void;
    fileList: UploadFile[];
    isImporting: boolean;
    isEditMode: boolean;
    handleReset: () => void
}

export const withServiceItemMasterLogic = <P extends object>(
    WrappedComponent: React.ComponentType<P & WithServiceItemMasterLogicProps>,
) => {
    const ServiceItemMasterWithLogic: React.FC<P> = (props) => {
        const [form] = Form.useForm();
        const { userData } = useAuth();
        const [isImportModalOpen, setIsImportModalOpen] = useState(false);
        const [fileList, setFileList] = useState<UploadFile[]>([]);
        const [isImporting, setIsImporting] = useState(false);
        const [isEditMode, setIsEditMode] = useState(false);
        const location = useLocation();
        const navigate = useNavigate();
        const fetcher = (url: string) => axios.get(url).then((res) => res.data);
        const { data: brandsData } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/service-item-master/brands`, fetcher);
        const { data: modelsData } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/service-item-master/models`, fetcher);
        const { data: warehouseData } = useSWR(
            userData?.warehouse_code
                ? `${process.env.REACT_APP_BACKEND_URL}/api/service-item-master/warehouse-name?code=${userData.warehouse_code}`
                : null,
            fetcher,
        );

        const frt = Form.useWatch('frt', form);
        const priceFlatRate = Form.useWatch('price_flat_rate', form);

        useEffect(() => {
            const parsedFrt = parseFloat(frt || '0');
            const parsedPriceFlatRate = priceFlatRate || 0;
            if (!isNaN(parsedFrt) && !isNaN(parsedPriceFlatRate)) {
                const calculatedPrice = parsedFrt * parsedPriceFlatRate;
                if (form.getFieldValue('price') !== calculatedPrice) {
                    form.setFieldsValue({ price: calculatedPrice });
                }
            } else {
                if (form.getFieldValue('price') !== 0) {
                    form.setFieldsValue({ price: 0 });
                }
            }
        }, [frt, priceFlatRate, form]);

        const handleReset = () => {
            form.resetFields();
            setIsEditMode(false);
            const initialValues: any = { status: "1" };
            if (warehouseData?.warehouse_name) {
                initialValues.warehouse = warehouseData.warehouse_name;
            } else if (userData?.warehouse_code) {
                initialValues.warehouse = userData.warehouse_code;
            }
            form.setFieldsValue(initialValues);
            navigate(location.pathname, { replace: true, state: null });
        };

        useEffect(() => {
            if (location.state && location.state.item_code) {
                form.setFieldsValue(location.state);
                setIsEditMode(true);
            } else {
                setIsEditMode(false);
                const initialValues: any = { status: "1" };
                if (warehouseData?.warehouse_name) {
                    initialValues.warehouse = warehouseData.warehouse_name;
                } else if (userData?.warehouse_code) {
                    initialValues.warehouse = userData.warehouse_code;
                }
                form.setFieldsValue(initialValues);
            }
        }, [form, userData, warehouseData, location.state]);

        const handleSubmit = async () => {
            try {
                const values = await form.validateFields();
                const {
                    ...restValues
                } = values;

                const payload = {
                    ...restValues,
                    warehouse: userData?.warehouse_code
                };

                if (isEditMode) {
                    await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/service-item-master`, payload);
                    message.success("Data updated successfully!");
                } else {
                    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/service-item-master`, payload);
                    message.success("Data saved successfully!");
                }

                form.resetFields();
                setIsEditMode(false);
                form.setFieldsValue({ status: "1", warehouse: warehouseData?.warehouse_name || userData?.warehouse_code });
            } catch (error) {
                console.error("Submit Error:", error);
                message.error("Failed to save data.");
            }
        };

        const handleExportTemplate = () => {
            const headers = [
                "item_code",
                "item_name",
                "frt",
                "hpp",
                "price_flat_rate",
                "price",
                "model",
                "brand",
                "remark", ,
            ];
            const ws = XLSX.utils.aoa_to_sheet([headers]);
            const brandRows =
                brandsData?.map((item: { label: string; value: string }) => [`${item.label} = ${item.value}`]) || [];
            const brandColumnData = [["Brand"], ...brandRows];
            const targetColIndex = 14;
            XLSX.utils.sheet_add_aoa(ws, brandColumnData, { origin: { r: 0, c: targetColIndex } });
            const modelRows =
                modelsData?.map((item: { label: string; value: string }) => [`${item.label} = ${item.value}`]) || [];
            const modelColumnData = [["MODEL"], ...modelRows];
            const modelTargetColIndex = targetColIndex + 2;
            XLSX.utils.sheet_add_aoa(ws, modelColumnData, { origin: { r: 0, c: modelTargetColIndex } });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Template");
            XLSX.writeFile(wb, "template_item_master.xlsx");
        };

        const handleOpenImportModal = () => setIsImportModalOpen(true);
        const handleCloseImportModal = () => {
            setIsImportModalOpen(false);
            setFileList([]);
            setIsImporting(false);
        };

        const handleImportSubmit = async () => {
            if (fileList.length === 0) {
                notification.warning({ message: "Warning", description: "Please select a file first." });
                return;
            }

            setIsImporting(true);
            await new Promise((resolve) => setTimeout(resolve, 4000));

            const file = (fileList[0].originFileObj || fileList[0]) as File;
            const reader = new FileReader();
            reader.onload = async (e) => {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const rawData: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
                const jsonData = rawData.map((row) => {
                    const newRow: any = {};
                    Object.keys(row).forEach((key) => {
                        newRow[key.trim()] = row[key];
                    });
                    return newRow;
                });

                const requiredFields = ["item_code", "item_name", "frt", "price_flat_rate", "price", "model", "brand"];

                for (let i = 0; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    const rowNum = i + 2;

                    const isRowEmpty = requiredFields.every(field => !row[field] || row[field].toString().trim() === "");
                    if (isRowEmpty) continue;

                    for (const field of requiredFields) {
                        const value = row[field];
                        if (value === undefined || value === null || value.toString().trim() === "") {
                            console.error(`Validation Error at Row ${rowNum}, Field: ${field}`, row);

                            notification.error({
                                message: "Validation Error",
                                description: `Data cannot be empty. Check Row: ${rowNum}, Column: ${field}. (Check for Merged Cells or Hidden Spaces)`,
                                duration: 5,
                            });
                            setIsImporting(false);
                            return;
                        }
                    }
                }

                const payload = jsonData
                    .filter((row) => row.item_code && row.item_code.toString().trim() !== "")
                    .map((row) => {
                        const {
                            ...restRow
                        } = row;

                        return {
                            ...restRow,
                            status: "1",
                            warehouse: userData?.warehouse_code
                        };
                    });
                try {
                    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/service-item-master/import`, { data: payload });
                    message.success("Data imported successfully!");
                    setIsImporting(false);
                    handleCloseImportModal();
                } catch (error: any) {
                    console.error("Import Error:", error);
                    setIsImporting(false);
                    if (error.response && error.response.status === 409 && error.response.data.data) {
                        const errorData = error.response.data.data;
                        const downloadErrorFile = () => {
                            const excelData = errorData.map((row: any) => {
                                const newRow: any = {};
                                Object.keys(row).forEach((key) => {
                                    if (
                                        !key.startsWith("__EMPTY") &&
                                        key !== "import_error" &&
                                        key !== "Brand" &&
                                        key !== "MODEL" &&
                                        key !== "status" &&
                                        key !== "warehouse"
                                    ) {
                                        newRow[key] = row[key];
                                    }
                                });
                                newRow["Error Description"] = row.import_error;
                                return newRow;
                            });

                            const headers = [
                                "item_code",
                                "item_name",
                                "frt",
                                "hpp",
                                "price_flat_rate",
                                "price",
                                "Error Description"
                            ];

                            const ws = XLSX.utils.json_to_sheet(excelData, { header: headers });
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, "Error Report");
                            XLSX.writeFile(wb, "import_error_report.xlsx");
                        };
                        Modal.error({
                            title: "Import Failed",
                            content: (
                                <div
                                    style={{ color: 'red', cursor: 'pointer', fontWeight: 'bold' }}
                                    onClick={() => {
                                        downloadErrorFile();
                                        Modal.destroyAll();
                                    }}
                                >
                                    Upload failed, check file
                                </div>
                            ),
                            okText: "Download Error Report",
                            okType: 'danger',
                            onOk: downloadErrorFile,
                        });

                        return;
                    }
                    notification.error({ message: "Import Failed", description: "Failed to upload data to server." });
                }
            };

            reader.readAsArrayBuffer(file);
        };

        const uploadProps: UploadProps = {
            name: "file",
            multiple: false,
            fileList: fileList,
            beforeUpload: (file) => {
                const isXls =
                    file.type === "application/vnd.ms-excel" ||
                    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                    file.name.endsWith(".xls") ||
                    file.name.endsWith(".xlsx");

                if (!isXls) {
                    notification.error({ message: "Invalid File Type", description: "You can only upload XLS/XLSX files!" });
                    return Upload.LIST_IGNORE;
                }

                if (fileList.length > 0) {
                    Modal.confirm({
                        title: "Confirmation",
                        content: "The existing file will be replaced! Are you sure?",
                        onOk: () => {
                            setFileList([file]);
                            message.success(`${file.name} file selected successfully.`);
                        },
                        onCancel: () => {
                        }
                    });
                    return Upload.LIST_IGNORE;
                }

                setFileList([file]);
                message.success(`${file.name} file selected successfully.`);
                return false;
            },
            onRemove: () => {
                setFileList([]);
            },
            onDrop(e) {
                console.log("Dropped files", e.dataTransfer.files);
            },
        };

        const logicProps: WithServiceItemMasterLogicProps = {
            form,
            brands: brandsData || [],
            models: modelsData || [],
            warehouseName: warehouseData?.warehouse_name || "",
            handleSubmit,
            handleExportTemplate,
            isImportModalOpen,
            handleOpenImportModal,
            handleCloseImportModal,
            uploadProps,
            handleImportSubmit,
            fileList,
            isImporting,
            isEditMode,
            handleReset,
        };

        return <WrappedComponent {...(props as P)} {...logicProps} />;
    };

    return ServiceItemMasterWithLogic;
};
